/**
 * DepartmentsGate: capability gate for Departments pages.
 *
 * Route-level ProtectedRoute only knows roles, but Departments capabilities are "role OR right"
 * (a user may hold the Department right without a Departments role), so pages ask
 * useDepartmentsPermissions.
 */

import type { ReactNode } from 'react';
import { IconLock } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { useDepartmentsPermissions, type DepartmentsPermissions } from '@/hooks/departments/useDepartmentsPermissions';

interface DepartmentsGateProps {
  allow: (p: DepartmentsPermissions) => boolean;
  children: ReactNode;
}

export function DepartmentsGate({ allow, children }: DepartmentsGateProps) {
  const perms = useDepartmentsPermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You do not have permission to open this page. Ask an administrator for access to Departments."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}
